import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import './UserFormModal.css';

function UserFormModal({ mode, user, onClose, onSave }) {
  const { user: currentUser } = useAuth();
  const isSuperAdmin = currentUser?.role === 'super_admin';

  const [formData, setFormData] = useState({
    username: '',
    email: '',
    displayName: '',
    role: 'user',
    isActive: true,
    notifyRoundReminders: true,
    notifyMatchReminders: true
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (mode === 'edit' && user) {
      setFormData({
        username: user.username || '',
        email: user.email || '',
        displayName: user.display_name || '',
        role: user.role || 'user',
        isActive: user.is_active !== undefined ? user.is_active : true,
        notifyRoundReminders: user.notify_round_reminders !== undefined ? user.notify_round_reminders : true,
        notifyMatchReminders: user.notify_match_reminders !== undefined ? user.notify_match_reminders : true
      });
    }
  }, [mode, user]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.username || formData.username.trim().length < 3) {
      newErrors.username = 'Username must be at least 3 characters';
    }

    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      await onSave(formData);
      onClose();
    } catch (error) {
      console.error('Failed to save user:', error);
      setErrors({
        submit: error.response?.data?.error || 'Failed to save user'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    // Clear error for this field when user types
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-container" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{mode === 'create' ? 'Create New User' : 'Edit User'}</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>

        <form onSubmit={handleSubmit} className="user-form">
          {/* Username */}
          <div className="form-group">
            <label htmlFor="username">
              Username <span className="required">*</span>
            </label>
            <input
              type="text"
              id="username"
              name="username"
              value={formData.username}
              onChange={handleChange}
              disabled={mode === 'edit'}
              className={errors.username ? 'error' : ''}
              placeholder="Enter username"
            />
            {errors.username && <span className="error-message">{errors.username}</span>}
            {mode === 'edit' && <span className="field-note">Username cannot be changed</span>}
          </div>

          {/* Email */}
          <div className="form-group">
            <label htmlFor="email">
              Email <span className="required">*</span>
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className={errors.email ? 'error' : ''}
              placeholder="user@example.com"
            />
            {errors.email && <span className="error-message">{errors.email}</span>}
          </div>

          {/* Display Name */}
          <div className="form-group">
            <label htmlFor="displayName">Display Name</label>
            <input
              type="text"
              id="displayName"
              name="displayName"
              value={formData.displayName}
              onChange={handleChange}
              placeholder="Optional display name"
            />
          </div>

          {/* Role - Only for super_admin */}
          {isSuperAdmin && (
            <div className="form-group">
              <label htmlFor="role">Role</label>
              <select
                id="role"
                name="role"
                value={formData.role}
                onChange={handleChange}
              >
                <option value="user">User</option>
                <option value="admin">Admin</option>
                <option value="super_admin">Super Admin</option>
              </select>
            </div>
          )}

          {/* Active Status */}
          <div className="form-group checkbox-group">
            <label>
              <input
                type="checkbox"
                name="isActive"
                checked={formData.isActive}
                onChange={handleChange}
              />
              <span>Active Account</span>
            </label>
            <span className="field-note">
              Inactive users cannot log in. Note: Users also need a verified email and unlocked account to log in.
            </span>
          </div>

          {/* Email Notification Preferences - Only in edit mode */}
          {mode === 'edit' && (
            <>
              <div className="form-section-title">Email Notification Preferences</div>

              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    name="notifyRoundReminders"
                    checked={formData.notifyRoundReminders}
                    onChange={handleChange}
                  />
                  <span>Round Reminder Emails</span>
                </label>
                <span className="field-note">User receives email 24 hours before each round starts</span>
              </div>

              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    name="notifyMatchReminders"
                    checked={formData.notifyMatchReminders}
                    onChange={handleChange}
                  />
                  <span>Match Reminder Emails</span>
                </label>
                <span className="field-note">User receives email 1 hour before each match if pick not made</span>
              </div>
            </>
          )}

          {/* Submit Error */}
          {errors.submit && (
            <div className="error-message submit-error">{errors.submit}</div>
          )}

          {/* Form Actions */}
          <div className="form-actions">
            <button type="button" onClick={onClose} className="btn btn-cancel" disabled={loading}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Saving...' : mode === 'create' ? 'Create User' : 'Save Changes'}
            </button>
          </div>

          {mode === 'create' && (
            <div className="form-info">
              A password reset email will be sent to the user automatically.
            </div>
          )}
        </form>
      </div>
    </div>
  );
}

export default UserFormModal;
